// ============================================================================
// SectionsAndCategoriesStep - Combined component for managing sections and categories
// ============================================================================
// This step allows users to define both:
// - Sections: Physical/logical divisions for audit navigation
// - Categories: Conceptual groupings for analysis and reporting
// ============================================================================

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Alert
} from '@mui/material';
import {
  Room as RoomIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { Section, Category } from '../FormBuilder';
import SectionsStep from './SectionsStep';
import CategoriesStep from './CategoriesStep';

interface SectionsAndCategoriesStepProps {
  sections: Section[];
  categories: Category[];
  onSectionsChange: (sections: Section[]) => void;
  onCategoriesChange: (categories: Category[]) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sections-categories-tabpanel-${index}`}
      aria-labelledby={`sections-categories-tab-${index}`}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const SectionsAndCategoriesStep: React.FC<SectionsAndCategoriesStepProps> = ({
  sections,
  categories,
  onSectionsChange,
  onCategoriesChange
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      {/* Información explicativa */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Secciones:</strong> Definen las áreas físicas o lógicas de la auditoría 
          (ej: Cocina, Salón, Baño). Se usan para navegar durante la ejecución.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Categorías:</strong> Agrupaciones conceptuales para análisis y reportes 
          (ej: Limpieza, Seguridad, Servicio). Ayudan a organizar los resultados.
        </Typography>
      </Alert>

      {/* Tabs de navegación */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            icon={<RoomIcon />}
            label={`Secciones (${sections.length})`}
            iconPosition="start"
            id="sections-categories-tab-0"
            aria-controls="sections-categories-tabpanel-0"
          />
          <Tab
            icon={<CategoryIcon />}
            label={`Categorías (${categories.length})`}
            iconPosition="start"
            id="sections-categories-tab-1"
            aria-controls="sections-categories-tabpanel-1"
          />
        </Tabs>
      </Paper>

      {/* Contenido de los tabs */}
      <TabPanel value={activeTab} index={0}>
        <SectionsStep
          sections={sections}
          onChange={onSectionsChange}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <CategoriesStep
          categories={categories}
          onChange={onCategoriesChange}
        />
      </TabPanel>
    </Box>
  );
};

export default SectionsAndCategoriesStep;
